import { useState } from 'react'
import { AlertTriangle, CheckCircle, XCircle, RefreshCw, Eye } from 'lucide-react'
import { useBuilder } from '../../context/BuilderContext'

function getContrastRatio(hex1, hex2) {
  const lum = (hex) => {
    const rgb = hex.replace('#', '').match(/.{2}/g)
    if (!rgb || rgb.length < 3) return 0
    const [r, g, b] = rgb.map(c => {
      const v = parseInt(c, 16) / 255
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
    })
    return 0.2126 * r + 0.7152 * g + 0.0722 * b
  }
  const l1 = lum(hex1)
  const l2 = lum(hex2)
  const lighter = Math.max(l1, l2)
  const darker = Math.min(l1, l2)
  return (lighter + 0.05) / (darker + 0.05)
}

function normalizeColor(color) {
  if (!color) return null
  const s = color.trim().toLowerCase()
  if (/^#[0-9a-f]{6}$/.test(s)) return s
  if (/^#[0-9a-f]{3}$/.test(s)) {
    return '#' + s[1] + s[1] + s[2] + s[2] + s[3] + s[3]
  }
  const named = {
    white: '#ffffff', black: '#000000', red: '#ff0000', blue: '#0000ff',
    green: '#008000', yellow: '#ffff00', gray: '#808080', grey: '#808080',
    orange: '#ffa500', purple: '#800080'
  }
  return named[s] || null
}

function analyzeAccessibility(blocks, bodySettings) {
  const issues = []
  let score = 100

  const bs = bodySettings || {}
  const contentBg = normalizeColor(bs.contentBackgroundColor) || '#ffffff'

  // Check each block
  blocks.forEach((block, idx) => {
    const s = block.settings
    const blockLabel = `Block ${idx + 1} (${block.type})`

    // Alt text on images
    if (block.type === 'image') {
      if (s.src && !s.alt) {
        score -= 8
        issues.push({
          type: 'error',
          message: `${blockLabel}: Image missing alt text`,
          tip: 'Add descriptive alt text for screen readers'
        })
      } else if (s.src && s.alt) {
        issues.push({
          type: 'pass',
          message: `${blockLabel}: Image has alt text`,
          tip: ''
        })
      }
    }

    // Color contrast for text blocks
    if (block.type === 'text' || block.type === 'heading') {
      const textColor = normalizeColor(s.color) || '#000000'
      const bgColor = normalizeColor(s.backgroundColor) || contentBg

      const ratio = getContrastRatio(textColor, bgColor)
      const minSize = block.type === 'heading' ? 18 : 14
      const fontSize = parseInt(s.fontSize) || minSize
      const isLargeText = fontSize >= 18 || (fontSize >= 14 && (s.fontWeight === 'bold' || s.fontWeight === '700'))
      const requiredRatio = isLargeText ? 3 : 4.5

      if (ratio < requiredRatio) {
        score -= 10
        issues.push({
          type: 'error',
          message: `${blockLabel}: Low contrast ratio (${ratio.toFixed(1)}:1)`,
          tip: `WCAG AA requires ${requiredRatio}:1 for ${isLargeText ? 'large' : 'normal'} text`
        })
      } else if (ratio < 7) {
        issues.push({
          type: 'info',
          message: `${blockLabel}: Contrast ratio ${ratio.toFixed(1)}:1 (AA pass)`,
          tip: 'Meets AA but not AAA standard'
        })
      } else {
        issues.push({
          type: 'pass',
          message: `${blockLabel}: Excellent contrast (${ratio.toFixed(1)}:1)`,
          tip: ''
        })
      }
    }

    // Button contrast and link text
    if (block.type === 'button') {
      const btnColor = normalizeColor(s.color) || '#ffffff'
      const btnBg = normalizeColor(s.backgroundColor) || '#6366f1'
      const ratio = getContrastRatio(btnColor, btnBg)

      if (ratio < 4.5) {
        score -= 10
        issues.push({
          type: 'error',
          message: `${blockLabel}: Button text contrast too low (${ratio.toFixed(1)}:1)`,
          tip: 'Button text should have at least 4.5:1 contrast ratio'
        })
      } else {
        issues.push({
          type: 'pass',
          message: `${blockLabel}: Button contrast OK (${ratio.toFixed(1)}:1)`,
          tip: ''
        })
      }

      if (s.text && /^(click here|here|link|button|submit)$/i.test(s.text.trim())) {
        score -= 5
        issues.push({
          type: 'warning',
          message: `${blockLabel}: Generic button text "${s.text}"`,
          tip: 'Use descriptive text like "Download Report" instead of "Click Here"'
        })
      }
    }

    // Font size checks
    if ((block.type === 'text' || block.type === 'heading') && s.fontSize) {
      const size = parseInt(s.fontSize)
      if (size < 12) {
        score -= 5
        issues.push({
          type: 'warning',
          message: `${blockLabel}: Font size ${size}px is very small`,
          tip: 'Minimum recommended font size is 14px for body text'
        })
      }
    }

    // Link in text content
    if (block.type === 'text' && s.content) {
      const linkRegex = /<a[^>]*>(.*?)<\/a>/gi
      let match
      while ((match = linkRegex.exec(s.content)) !== null) {
        const linkText = match[1].replace(/<[^>]*>/g, '').trim()
        if (/^(click here|here|link|read more|more)$/i.test(linkText)) {
          score -= 3
          issues.push({
            type: 'warning',
            message: `${blockLabel}: Non-descriptive link text "${linkText}"`,
            tip: 'Link text should describe the destination, not just say "click here"'
          })
        }
      }
    }
  })

  // Global checks
  const hasHeading = blocks.some(b => b.type === 'heading')
  if (!hasHeading && blocks.length > 2) {
    score -= 5
    issues.push({
      type: 'warning',
      message: 'No heading block found',
      tip: 'Use headings to create a logical content hierarchy'
    })
  }

  // Check for sufficient text content
  const textBlocks = blocks.filter(b => b.type === 'text' || b.type === 'heading')
  const imageBlocks = blocks.filter(b => b.type === 'image')
  if (imageBlocks.length > 0 && textBlocks.length === 0) {
    score -= 15
    issues.push({
      type: 'error',
      message: 'Email contains only images, no text',
      tip: 'Screen readers cannot read image-only emails. Add text alternatives.'
    })
  }

  // Language / reading level (simple heuristic)
  let allText = ''
  blocks.forEach(b => {
    if (b.settings.content) allText += b.settings.content.replace(/<[^>]*>/g, '') + ' '
    if (b.settings.text) allText += b.settings.text + ' '
  })
  const words = allText.trim().split(/\s+/).filter(w => w.length > 0)
  const longWords = words.filter(w => w.length > 12)
  if (words.length > 20 && longWords.length / words.length > 0.15) {
    score -= 3
    issues.push({
      type: 'info',
      message: 'Content may be difficult to read',
      tip: 'Consider simplifying language — many readers scan emails quickly'
    })
  }

  if (blocks.length === 0) {
    return { score: 0, issues: [{ type: 'info', message: 'Add blocks to check accessibility', tip: '' }] }
  }

  score = Math.min(Math.max(score, 0), 100)
  return { score, issues }
}

export default function AccessibilityChecker() {
  const { blocks, bodySettings } = useBuilder()
  const [result, setResult] = useState(null)

  const runCheck = () => {
    setResult(analyzeAccessibility(blocks, bodySettings))
  }

  const getScoreColor = (score) => {
    if (score >= 80) return '#22c55e'
    if (score >= 50) return '#f59e0b'
    return '#dc2626'
  }

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Good'
    if (score >= 50) return 'Needs Work'
    return 'Poor'
  }

  const getIcon = (type) => {
    switch (type) {
      case 'error': return <XCircle size={14} style={{ color: '#dc2626', flexShrink: 0 }} />
      case 'warning': return <AlertTriangle size={14} style={{ color: '#f59e0b', flexShrink: 0 }} />
      case 'pass': return <CheckCircle size={14} style={{ color: '#22c55e', flexShrink: 0 }} />
      default: return <Eye size={14} style={{ color: '#6366f1', flexShrink: 0 }} />
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600' }}>Accessibility Checker</h4>
      <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>
        Check your email for WCAG accessibility issues
      </p>

      <button
        onClick={runCheck}
        disabled={blocks.length === 0}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
          padding: '0.6rem 1rem', border: '1px solid var(--color-primary)',
          borderRadius: '8px', background: 'rgba(99,102,241,0.05)',
          color: 'var(--color-primary)', cursor: 'pointer', fontSize: '0.85rem',
          fontWeight: '600', opacity: blocks.length === 0 ? 0.5 : 1
        }}
      >
        <RefreshCw size={15} /> Run Check
      </button>

      {result && (
        <>
          {/* Score */}
          <div style={{
            textAlign: 'center', padding: '1rem',
            background: `${getScoreColor(result.score)}08`,
            border: `1px solid ${getScoreColor(result.score)}30`,
            borderRadius: '10px'
          }}>
            <div style={{
              fontSize: '2rem', fontWeight: '800',
              color: getScoreColor(result.score),
              lineHeight: 1
            }}>
              {result.score}
            </div>
            <div style={{
              fontSize: '0.7rem', fontWeight: '600',
              color: getScoreColor(result.score),
              marginTop: '0.25rem', textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              {getScoreLabel(result.score)}
            </div>
            <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: '0.25rem' }}>
              Higher score = better accessibility
            </div>
          </div>

          {/* Issues */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {result.issues.map((issue, idx) => (
              <div key={idx} style={{
                display: 'flex', flexDirection: 'column', gap: '0.2rem',
                padding: '0.5rem 0.6rem', borderRadius: '6px',
                background: issue.type === 'error' ? '#fef2f2' : issue.type === 'warning' ? '#fffbeb' : issue.type === 'pass' ? '#f0fdf4' : '#f0f0ff',
                fontSize: '0.75rem', lineHeight: '1.4'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', color: '#475569' }}>
                  {getIcon(issue.type)}
                  <span>{issue.message}</span>
                </div>
                {issue.tip && (
                  <div style={{ marginLeft: '1.25rem', fontSize: '0.68rem', color: '#94a3b8', fontStyle: 'italic' }}>
                    {issue.tip}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
